<template>
  <WelcomeHeader />
  <GuidelineNav current="math" />

  <!-- ═══ HERO ═══ -->
  <section class="hero-section">
    <div class="hero-noise"></div>
    <div class="container hero-inner">
      <div class="hero-badge">বিসিএস প্রস্তুতি গাইড · সংস্করণ ৪.০</div>
      <h1 class="hero-title">গাণিতিক যুক্তি ও মানসিক দক্ষতা <span class="hero-accent">গাইডলাইন</span></h1>
      <p class="hero-sub">বিসিএস ৩৫–৫০ — একুরেট টপিকভিত্তিক প্রশ্ন বিশ্লেষণ</p>
      <div class="hero-stats">
        <div class="hstat"><div class="hstat-num">৩৫</div><div class="hstat-lbl">মোট মার্ক</div></div>
        <div class="hstat-sep"></div>
        <div class="hstat"><div class="hstat-num">২০</div><div class="hstat-lbl">গাণিতিক যুক্তি</div></div>
        <div class="hstat-sep"></div>
        <div class="hstat"><div class="hstat-num">১৫</div><div class="hstat-lbl">মানসিক দক্ষতা</div></div>
        <div class="hstat-sep"></div>
        <div class="hstat"><div class="hstat-num">৯ লাখ+</div><div class="hstat-lbl">অ্যাপ ডাউনলোড</div></div>
      </div>
    </div>
  </section>

  <!-- ═══ MARKS BREAKDOWN ═══ -->
  <div class="marks-bar">
    <div class="container">
      <div class="marks-header">মার্ক বিভাজন (মোট = ৩৫)</div>
      <div class="marks-grid">
        <div class="mk-item"><div class="mk-num">২০</div><div class="mk-lbl">গাণিতিক যুক্তি</div></div>
        <div class="mk-item"><div class="mk-num">১৫</div><div class="mk-lbl">মানসিক দক্ষতা</div></div>
      </div>
    </div>
  </div>

  <!-- ═══ LEGEND BAR ═══ -->
  <div class="legend-bar">
    <div class="legend-inner">
      <span class="legend-label">হিটম্যাপ স্কেল:</span>
      <span class="lc h0">০</span>
      <span class="lc h1">১</span>
      <span class="lc h2">২</span>
      <span class="lc h3">৩+</span>
      <span class="legend-sep">|</span>
      <span class="pri-tag p3">★★★ অবশ্যই</span>
      <span class="pri-tag p2">★★ গুরুত্বপূর্ণ</span>
      <span class="pri-tag p1">★ এক নজর</span>
    </div>
  </div>

  <!-- ═══ MAIN ═══ -->
  <section class="main-section">
    <div class="container">
      <div class="tab-content">

        <!-- ───────── গাণিতিক যুক্তি ───────── -->
        <div class="subj-tag">গাণিতিক যুক্তি · ২০ মার্ক</div>

        <!-- Syllabus topic groups (V3) -->
        <div class="syllabus-card">
          <h3 class="chart-title">সিলেবাস অনুযায়ী টপিক গ্রুপ — গাণিতিক যুক্তি (মোট ২০ মার্ক)</h3>
          <div class="syl-list">
            <div class="syl-row" v-for="(g, i) in mathSyllabus" :key="i">
              <div class="syl-mark"><span class="sm-num">{{ toBengali(g.marks) }}</span><span class="sm-lbl">মার্ক</span></div>
              <div class="syl-topics">{{ g.topics }}</div>
            </div>
          </div>
        </div>

        <!-- Bar Chart -->
        <div class="chart-card">
          <h3 class="chart-title">শীর্ষ টপিক — ১৪টি বিসিএসে (৩৫–৫০) মোট প্রশ্ন</h3>
          <div class="bar-list">
            <div v-for="row in mathChart" :key="row.topic" class="bar-row">
              <div class="bar-label">{{ row.topic }}</div>
              <div class="bar-track">
                <div class="bar-fill" :style="{ width: (row.total / 27 * 100) + '%' }" :class="'bp' + row.priority"></div>
              </div>
              <div class="bar-val">{{ toBengali(row.total) }}</div>
            </div>
          </div>
        </div>

        <!-- Heatmap -->
        <div class="section-head">
          <h2>টপিকভিত্তিক প্রশ্ন হিটম্যাপ — গাণিতিক যুক্তি</h2>
          <p>বিসিএস ৩৫ থেকে ৫০ পর্যন্ত ১৪টি পরীক্ষার প্রতিটিতে কোন টপিক থেকে কয়টি প্রশ্ন এসেছে</p>
        </div>
        <div class="table-wrap">
          <table class="heat-table">
            <thead>
              <tr>
                <th class="t-topic">টপিক / অধ্যায়</th>
                <th v-for="y in years" :key="y" :class="y >= 46 ? 't-new' : ''">{{ toBengali(y) }}</th>
                <th class="t-hist">মোট</th>
                <th class="t-appr">১৪-এ<br>কতবার</th>
                <th class="t-pri">গুরুত্ব</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in mathData" :key="row.topic">
                <td class="t-name">{{ row.topic }}</td>
                <template v-if="row.y">
                  <td v-for="(val, i) in row.y" :key="'y' + i" :class="heatClass(val)">{{ val > 0 ? toBengali(val) : '' }}</td>
                </template>
                <td v-else class="t-na" colspan="11">—</td>
                <template v-if="row.r">
                  <td v-for="(val, i) in row.r" :key="'r' + i" :class="heatClass(val) + ' t-recent'">{{ val > 0 ? toBengali(val) : '' }}</td>
                </template>
                <td v-else class="t-na t-recent" colspan="3">—</td>
                <td class="t-hist-cell">{{ toBengali(row.total) }}</td>
                <td class="t-appr-cell">{{ toBengali(row.appr) }}</td>
                <td class="t-pri-cell"><span :class="'pb p' + row.priority">{{ priorityLabel(row.priority) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ───────── মানসিক দক্ষতা ───────── -->
        <div class="subj-tag mt">মানসিক দক্ষতা · ১৫ মার্ক</div>

        <!-- Bar Chart -->
        <div class="chart-card">
          <h3 class="chart-title">শীর্ষ টপিক — ১৪টি বিসিএসে (৩৫–৫০) মোট প্রশ্ন</h3>
          <div class="bar-list">
            <div v-for="row in mentalChart" :key="row.topic" class="bar-row">
              <div class="bar-label">{{ row.topic }}</div>
              <div class="bar-track">
                <div class="bar-fill" :style="{ width: (row.total / 15 * 100) + '%' }" :class="'bp' + row.priority"></div>
              </div>
              <div class="bar-val">{{ toBengali(row.total) }}</div>
            </div>
          </div>
        </div>

        <!-- Heatmap -->
        <div class="section-head">
          <h2>টপিকভিত্তিক প্রশ্ন হিটম্যাপ — মানসিক দক্ষতা</h2>
          <p>বিসিএস ৩৫ থেকে ৫০ পর্যন্ত ১৪টি পরীক্ষার প্রতিটিতে কোন টপিক থেকে কয়টি প্রশ্ন এসেছে</p>
        </div>
        <div class="table-wrap">
          <table class="heat-table">
            <thead>
              <tr>
                <th class="t-topic">টপিক / অধ্যায়</th>
                <th v-for="y in years" :key="y" :class="y >= 46 ? 't-new' : ''">{{ toBengali(y) }}</th>
                <th class="t-hist">মোট</th>
                <th class="t-appr">১৪-এ<br>কতবার</th>
                <th class="t-pri">গুরুত্ব</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in mentalData" :key="row.topic">
                <td class="t-name">{{ row.topic }}</td>
                <template v-if="row.y">
                  <td v-for="(val, i) in row.y" :key="'y' + i" :class="heatClass(val)">{{ val > 0 ? toBengali(val) : '' }}</td>
                </template>
                <td v-else class="t-na" colspan="11">—</td>
                <template v-if="row.r">
                  <td v-for="(val, i) in row.r" :key="'r' + i" :class="heatClass(val) + ' t-recent'">{{ val > 0 ? toBengali(val) : '' }}</td>
                </template>
                <td v-else class="t-na t-recent" colspan="3">—</td>
                <td class="t-hist-cell">{{ toBengali(row.total) }}</td>
                <td class="t-appr-cell">{{ toBengali(row.appr) }}</td>
                <td class="t-pri-cell"><span :class="'pb p' + row.priority">{{ priorityLabel(row.priority) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Insight Cards -->
        <div class="insight-grid">
          <div class="ins-card must">
            <div class="ins-head">★★★ অবশ্যই পড়তে হবে</div>
            <ul>
              <li>বিন্যাস ও সমাবেশ, পরিসংখ্যান ও সম্ভাব্যতা</li>
              <li>সূচক ও লগারিদম</li>
              <li>অনুক্রম ও ধারা</li>
              <li>সেট</li>
              <li>সরল ও দ্বিঘাত সমীকরণ ও অসমতা</li>
              <li>বীজগাণিতিক সূত্রাবলী ও রাশিমালা</li>
              <li>সরল ও যৌগিক মুনাফা, লাভ-ক্ষতি</li>
              <li>ত্রিভুজ, চতুর্ভুজ ও জ্যামিতি</li>
              <li>সাদৃশ্য ও বৈসাদৃশ্য বিচার (মানসিক)</li>
              <li>সংখ্যার ধারণা ও সমীকরণ (মানসিক)</li>
              <li>বানান শুদ্ধকরণ ও বাক্য প্রকরণ (মানসিক)</li>
            </ul>
          </div>
          <div class="ins-card imp">
            <div class="ins-head">★★ গুরুত্বপূর্ণ</div>
            <ul>
              <li>বাস্তব সংখ্যা, লসাগু ও গসাগু</li>
              <li>শতকরা, অনুপাত ও সমানুপাত</li>
              <li>পরিমিতি ও বৃত্ত</li>
              <li>চিত্র গণনা ও প্রাসঙ্গিক চিত্র বাছাই (মানসিক)</li>
              <li>দর্পণ প্রতিবিম্ব (মানসিক)</li>
              <li>দিক নির্দেশনা (মানসিক)</li>
              <li>বার-তারিখ, সময় ও ঘড়ি বিষয়ক সমস্যা (মানসিক)</li>
            </ul>
          </div>
          <div class="ins-card low">
            <div class="ins-head">★ এক নজর দেখুন</div>
            <ul>
              <li>ঐকিক নিয়ম, বহুপদী উৎপাদক</li>
              <li>বৃত্ত সম্পর্কিত উপপাদ্য</li>
              <li>যন্ত্রাংশ ও সরঞ্জাম, সরল যন্ত্র</li>
              <li>রক্তের সম্পর্ক ও সম্পর্ক নির্ণয়</li>
              <li>Synonyms &amp; Antonyms</li>
              <li>সাধারণ বিজ্ঞানভিত্তিক যুক্তি</li>
            </ul>
          </div>
        </div>

        <!-- Combined Analysis -->
        <div class="analysis-box">
          <h4>📊 বিশ্লেষণ</h4>
          <p>গণিতের বিগত বছরের প্রশ্ন এনালাইসিস করলে দেখা যায় — <strong>সরল ও যৌগিক মুনাফা, বিন্যাস ও সমাবেশ, সম্ভাব্যতা, লাভ-ক্ষতি, অনুক্রম ও ধারা, সরল ও দ্বিপদী অসমতা, সূচক ও লগারিদম, বীজগাণিতিক সূত্রাবলী ও রাশিমালা, সেট ও ফাংশন, ত্রিভুজ ও চতুর্ভুজ</strong> — এই টপিকগুলো থেকে প্রতিবছরই এক বা একাধিক প্রশ্ন আসে। এগুলো মোস্ট ইম্পরট্যান্ট, খুব গুরুত্ব দিয়ে পড়তে হবে এবং রেগুলার চর্চায় রাখতে হবে।</p>
          <p>ঠিক তেমনি মানসিক দক্ষতায় — <strong>সাংকেতিক বিন্যাস ও বাক্য গঠন, সাদৃশ্য ও বৈসাদৃশ্য বিচার, বানান শুদ্ধকরণ ও বাক্য প্রকরণ, দর্পণে প্রতিবিম্ব, চিত্র গণনা ও প্রাসঙ্গিক চিত্র বাছাই, চিত্রে সংখ্যার যৌক্তিক অবস্থান নির্ণয়, শতকরা-ভগ্নাংশ ও অনুপাত, সংখ্যার ধারণা ও সমীকরণ</strong> — এই টপিকগুলো থেকে প্রায় প্রতিবছরই প্রশ্ন আসে। তাই খুব ভালোভাবে পড়ে রেগুলার চর্চায় রাখতে হবে।</p>
          <p><strong>বাস্তব সংখ্যা, ভগ্নাংশ ও সরলীকরণ, লসাগু-গসাগু, শতকরা, অনুপাত ও সমানুপাত, বৃত্ত</strong> — এগুলো থেকেও মাঝে মাঝে প্রশ্ন আসে বা আসতে পারে, তাই এগুলোও পড়ে রাখতে হবে।</p>
          <p class="mb-0"><strong>বর্গমূল, ঐকিক নিয়ম, গড়, পরিমাপ ও একক, বীজগাণিতীয় ভগ্নাংশ, বহুপদী উৎপাদক, ত্রিকোণমিতি</strong> — এসব থেকে প্রশ্ন আসে না বললেই চলে, তাই তুলনামূলক কম গুরুত্বপূর্ণ। তবুও সময় থাকলে একবার চর্চা করে নিতে পারেন।</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══ FREE PDF OPT-IN ═══ -->
  <GuidelineOptin subject="math" pdf="https://www.bcsprostuti.com/api6/storage/pdfs/math-guideline_2026_07_24_12_18_33.pdf" />

  <!-- ═══ REFERENCE BOOKS ═══ -->
  <section class="ref-section">
    <div class="container">
      <div class="section-head center">
        <h2>রেফারেন্স বই</h2>
        <p>পরীক্ষায় ভালো করার জন্য এই বইগুলো অনুসরণ করুন</p>
      </div>
      <div class="book-grid">
        <div class="book-card" v-for="b in refBooks" :key="b.title">
          <div class="book-spine"></div>
          <div class="book-info">
            <div class="book-title">{{ b.title }}</div>
            <div class="book-author">{{ b.author }}</div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══ TIPS ═══ -->
  <section class="tips-section">
    <div class="container">
      <div class="section-head center">
        <h2>পরামর্শ ও কৌশল</h2>
        <p>কীভাবে গাণিতিক যুক্তি ও মানসিক দক্ষতায় সর্বোচ্চ নম্বর তুলবেন</p>
      </div>
      <div class="tips-grid">
        <div class="tip-card" v-for="(tip, i) in tips" :key="i">
          <div class="tip-num">{{ toBengali(i + 1) }}</div>
          <p class="tip-text">{{ tip }}</p>
        </div>
      </div>
    </div>
  </section>

  <GuidelineRelated current="math" />

  <!-- ═══ CTA ═══ -->
  <section class="cta-section">
    <div class="container">
      <div class="cta-grid">
        <div class="cta-left">
          <div class="cta-tag">বিসিএস প্রস্তুতি অ্যাপ</div>
          <h2 class="cta-title">গাণিতিক যুক্তি ও মানসিক দক্ষতায় সর্বোচ্চ নম্বর তুলুন</h2>
          <ul class="cta-features">
            <li><span class="cf-icon">✓</span> টপিকভিত্তিক প্রশ্ন ব্যাঙ্ক ও ধাপে ধাপে সমাধান</li>
            <li><span class="cf-icon">✓</span> বিগত বিসিএসের সম্পূর্ণ প্রশ্নসমূহ ব্যাখ্যাসহ</li>
            <li><span class="cf-icon">✓</span> স্মার্ট প্রগ্রেস ট্র্যাকিং সিস্টেম</li>
            <li><span class="cf-icon">✓</span> লাইভ কোর্স ও মডেল টেস্ট</li>
          </ul>
          <div class="cta-actions">
            <a href="https://play.google.com/store/apps/details?id=com.bcsprostuti.tanim.bcsprostuti" target="_blank" class="cta-play" @click="trackAppInstall">
              <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Google Play" height="48">
            </a>
            <a href="/packages" class="cta-pkg" @click="trackInitiateCheckout">প্যাকেজ দেখুন →</a>
          </div>
          <p class="cta-count">৯ লাখ+ শিক্ষার্থীর বিশ্বাসের অ্যাপ</p>
        </div>
        <div class="cta-right">
          <div class="cta-stat-card"><div class="cs-num">৩৫</div><div class="cs-lbl">মোট মার্ক</div></div>
          <div class="cta-stat-card"><div class="cs-num">৩৫–৫০</div><div class="cs-lbl">বিসিএস বিশ্লেষণ</div></div>
          <div class="cta-stat-card"><div class="cs-num">৫০+টি</div><div class="cs-lbl">টপিক কভারেজ</div></div>
          <div class="cta-stat-card"><div class="cs-num">৯ লাখ+</div><div class="cs-lbl">অ্যাপ ডাউনলোড</div></div>
        </div>
      </div>
    </div>
  </section>

  <WelcomeFooter :hide-download="true" />

  <MessengerFloat subject="math" />
</template>

<script>
import WelcomeHeader from "@/components/WelcomePages/WelcomeHeader.vue";
import WelcomeFooter from "@/components/WelcomePages/WelcomeFooter.vue";
import GuidelineNav from "@/components/WelcomePages/GuidelineNav.vue";
import GuidelineRelated from "@/components/WelcomePages/GuidelineRelated.vue";
import GuidelineOptin from "@/components/WelcomePages/GuidelineOptin.vue";
import MessengerFloat from "@/components/WelcomePages/MessengerFloat.vue";
import * as pixels from "@/config/pixels";
import { computed, onMounted } from 'vue';

export default {
  components: { WelcomeHeader, WelcomeFooter, GuidelineNav, GuidelineRelated, GuidelineOptin, MessengerFloat },
  setup() {
    // ── প্রতি-বিসিএস প্রশ্নসংখ্যা (একীভূত হিটম্যাপ) ──
    //    y     = বিসিএস ৩৫,৩৬,৩৭,৩৮,৩৯,৪০,৪১,৪২,৪৩,৪৪,৪৫ — প্রতিটি পরীক্ষা আলাদা (V3_Math)।
    //    r     = সাম্প্রতিক ৪৬, ৪৭, ৫০ (BCS_Math_Accurate_Summary)। ডেটা না থাকলে null।
    //    total = ১৪টি পরীক্ষার সর্বমোট, appr = ১৪টির কতটিতে টপিকটি এসেছে। সাজানো: total অনুযায়ী।
    const mathData = [
      { topic: 'বিন্যাস ও সমাবেশ, পরিসংখ্যান ও সম্ভাব্যতা', y: [3,2,2,2,1,1,2,1,1,1,2], r: [3,2,4], total: 27, appr: 14, priority: 3 },
      { topic: 'সূচক ও লগারিদম', y: [2,2,2,2,1,2,2,1,1,2,2], r: [2,1,1], total: 23, appr: 14, priority: 3 },
      { topic: 'অনুক্রম ও ধারা', y: [1,1,2,1,1,1,1,1,1,2,1], r: [0,2,2], total: 17, appr: 13, priority: 3 },
      { topic: 'সেট', y: [1,1,1,1,1,1,0,0,2,1,1], r: [1,1,2], total: 14, appr: 12, priority: 3 },
      { topic: 'পরিমিতি', y: [1,1,1,1,1,0,1,2,0,0,1], r: [2,1,2], total: 14, appr: 11, priority: 2 },
      { topic: 'সরল ও দ্বিঘাত / দ্বিপদী অসমতা', y: [1,0,1,1,2,1,1,1,1,1,0], r: [1,1,1], total: 13, appr: 12, priority: 3 },
      { topic: 'বীজগাণিতিক সূত্রাবলী', y: [2,1,1,1,0,1,2,0,1,0,1], r: [1,1,1], total: 13, appr: 11, priority: 3 },
      { topic: 'রেখা, কোণ, ত্রিভুজ, চতুর্ভুজ ও জ্যামিতি', y: [0,1,0,1,0,1,1,0,1,3,1], r: [1,1,1], total: 12, appr: 10, priority: 3 },
      { topic: 'ল.সা.গু ও গ.সা.গু', y: [1,1,0,1,1,1,0,0,1,1,1], r: [0,0,1], total: 9, appr: 9, priority: 2 },
      { topic: 'সরল ও দ্বিঘাত সমীকরণ', y: [1,0,1,0,0,1,0,0,0,0,2], r: [2,0,2], total: 9, appr: 6, priority: 2 },
      { topic: 'সরল ও যৌগিক মুনাফা', y: [0,0,1,1,1,1,1,0,0,0,0], r: [1,1,1], total: 8, appr: 8, priority: 3 },
      { topic: 'লাভ ও ক্ষতি', y: [0,0,1,1,1,1,0,1,1,1,1], r: null, total: 8, appr: 8, priority: 3 },
      { topic: 'শতকরা', y: [1,2,0,1,0,0,2,1,0,0,0], r: [0,1,0], total: 8, appr: 6, priority: 2 },
      { topic: 'বীজগাণিতিক রাশি', y: [0,1,0,0,0,0,0,0,2,3,1], r: [0,0,1], total: 8, appr: 5, priority: 3 },
      { topic: 'বাস্তব সংখ্যা', y: [0,1,0,0,0,1,2,0,0,0,0], r: [1,1,1], total: 7, appr: 6, priority: 2 },
      { topic: 'অনুপাত ও সমানুপাত', y: [1,0,1,0,0,1,0,0,0,0,1], r: [1,0,1], total: 6, appr: 6, priority: 2 },
      { topic: 'বৃত্ত সংক্রান্ত উপপাদ্য', y: [0,0,1,0,0,0,0,1,1,0,0], r: [1,0,0], total: 4, appr: 4, priority: 1 },
      { topic: 'ত্রিকোণমিতি', y: [0,0,0,1,0,1,0,1,0,0,1], r: null, total: 4, appr: 4, priority: 1 },
      { topic: 'ঐকিক নিয়ম', y: [0,0,0,0,0,0,0,0,1,0,0], r: [0,1,0], total: 2, appr: 2, priority: 1 },
      { topic: 'বহুপদী উৎপাদক', y: [0,0,0,0,0,0,0,0,0,0,0], r: [0,1,0], total: 1, appr: 1, priority: 1 },
      { topic: 'সরল সহসমীকরণ', y: [0,1,0,0,0,0,0,0,0,0,0], r: null, total: 1, appr: 1, priority: 1 },
      { topic: 'পিথাগোরাসের উপপাদ্য', y: [0,0,0,0,0,0,0,0,0,0,0], r: null, total: 0, appr: 0, priority: 1 },
    ];

    // ── মানসিক দক্ষতা — একীভূত হিটম্যাপ (values: ৪৬/৪৭/৫০ | hist: ৩৫–৪৫ মোট | appr: ১১-এ কতবার) ──
    const mentalData = [
      { topic: 'সংখ্যার ধারণা ও সমীকরণ সম্পর্কিত বিষয়', y: [1,0,0,1,1,0,2,1,0,0,3], r: [3,1,2], total: 15, appr: 9, priority: 3 },
      { topic: 'সাঙ্কেতিক বিন্যাস, শব্দ ও বাক্য গঠন', y: [0,1,1,2,0,3,1,1,2,2,0], r: [0,1,0], total: 14, appr: 9, priority: 3 },
      { topic: 'চিত্র গণনা ও প্রাসঙ্গিক চিত্র বাছাই', y: [2,0,0,0,0,2,0,1,1,2,3], r: [1,2,0], total: 14, appr: 8, priority: 3 },
      { topic: 'সাদৃশ্য ও বৈসাদৃশ্য বিচার', y: [1,0,2,1,1,0,1,1,1,0,0], r: [2,3,0], total: 13, appr: 9, priority: 3 },
      { topic: 'চিত্রে সংখ্যার যৌক্তিক অবস্থান নির্ণয়', y: [1,3,1,0,2,1,1,0,1,1,0], r: [0,0,1], total: 12, appr: 9, priority: 3 },
      { topic: 'দর্পণে প্রতিবিম্ব', y: [1,3,0,1,0,1,1,0,0,1,1], r: [0,1,1], total: 11, appr: 9, priority: 3 },
      { topic: 'বানান শুদ্ধকরণ ও বাক্য প্রকরণ', y: [2,1,0,0,0,2,0,1,0,0,0], r: [1,2,2], total: 11, appr: 7, priority: 3 },
      { topic: 'অনুক্রম সম্পর্কিত সমস্যা', y: [2,2,0,1,0,0,0,1,2,1,0], r: null, total: 9, appr: 6, priority: 3 },
      { topic: 'বিবিধ', y: [1,0,0,1,1,0,1,1,0,1,2], r: null, total: 8, appr: 7, priority: 1 },
      { topic: 'শতকরা, ভগ্নাংশ ও অনুপাত', y: [2,1,1,0,1,0,0,0,1,0,0], r: [0,0,1], total: 7, appr: 6, priority: 3 },
      { topic: 'বার ও তারিখ নির্ণয়', y: [0,0,1,2,0,0,1,0,0,1,0], r: [0,0,2], total: 7, appr: 5, priority: 2 },
      { topic: 'ধারা সম্পর্কিত সমস্যা', y: [0,1,0,1,0,0,0,1,0,2,2], r: null, total: 7, appr: 5, priority: 2 },
      { topic: 'স্থানাঙ্ক ব্যবস্থা', y: [0,0,1,1,1,1,1,0,1,0,0], r: null, total: 6, appr: 6, priority: 2 },
      { topic: 'ভগ্নাংশ ও দশমিক সংখ্যা সমস্যাবলী', y: [1,0,1,1,1,1,1,0,0,0,0], r: null, total: 6, appr: 6, priority: 2 },
      { topic: 'রক্তের সম্পর্ক ও বিশেষ্যত্ব নির্ণয়', y: [0,0,0,0,0,0,1,0,1,1,0], r: [1,1,0], total: 5, appr: 5, priority: 2 },
      { topic: 'সিদ্ধান্ত গ্রহণ ও বিচার দক্ষতা', y: [1,0,0,0,0,1,0,1,0,0,0], r: [1,1,0], total: 5, appr: 5, priority: 2 },
      { topic: 'Spelling', y: [0,0,1,0,0,1,1,0,0,0,0], r: [1,1,0], total: 5, appr: 5, priority: 2 },
      { topic: 'ঘড়ি বিষয়ক অভীক্ষা', y: [0,0,0,0,1,0,1,0,0,1,0], r: [0,0,2], total: 5, appr: 4, priority: 2 },
      { topic: 'ভাবার্থ অনুধাবন ও সঠিক শব্দ', y: [0,0,0,0,0,0,0,1,1,1,1], r: null, total: 4, appr: 4, priority: 2 },
      { topic: 'সময় ও কাজ বিষয়ক সমস্যা', y: [0,0,0,1,0,0,1,0,1,0,0], r: [0,0,1], total: 4, appr: 4, priority: 2 },
      { topic: 'শতকরা ও ঐকিক', y: [0,0,0,1,0,0,1,0,0,0,1], r: [0,0,1], total: 4, appr: 4, priority: 2 },
      { topic: 'সমার্থক ও বিপরীতার্থক শব্দ', y: [0,0,1,1,1,0,0,0,1,0,0], r: null, total: 4, appr: 4, priority: 2 },
      { topic: 'Synonyms and Antonyms', y: [0,0,1,0,0,0,0,0,1,0,1], r: [1,0,0], total: 4, appr: 4, priority: 1 },
      { topic: 'সরল যন্ত্র ও জটিল যন্ত্র', y: [0,1,0,0,0,1,0,0,1,0,0], r: [1,0,0], total: 4, appr: 4, priority: 1 },
      { topic: 'বল বিভাজন নীতি', y: [0,0,2,0,0,1,0,0,0,1,0], r: null, total: 4, appr: 3, priority: 1 },
      { topic: 'দিক নির্দেশনা', y: null, r: [0,2,1], total: 3, appr: 2, priority: 2 },
      { topic: 'জ্যামিতিক সমস্যা', y: [0,1,1,0,0,0,0,0,0,0,0], r: null, total: 2, appr: 2, priority: 1 },
      { topic: 'যন্ত্রাংশ ও সরঞ্জাম', y: [0,0,1,0,0,0,0,0,0,0,0], r: [1,0,0], total: 2, appr: 2, priority: 1 },
      { topic: 'সমাবেশ (Permutation & Combination)', y: null, r: [1,0,0], total: 1, appr: 1, priority: 2 },
      { topic: 'শতকরা ও অনুপাত', y: null, r: [1,0,0], total: 1, appr: 1, priority: 2 },
      { topic: 'নল ও চৌবাচ্চা বিষয়ক সমস্যা', y: [0,0,0,0,0,0,0,0,0,0,1], r: null, total: 1, appr: 1, priority: 1 },
      { topic: 'বর্গমূল ও ঘনমূল নির্ণয়', y: [0,1,0,0,0,0,0,0,0,0,0], r: null, total: 1, appr: 1, priority: 1 },
      { topic: 'সাধারণ বিজ্ঞানভিত্তিক যুক্তি', y: null, r: [0,0,1], total: 1, appr: 1, priority: 1 },
    ];

    // কলামের বছর: ৩৫–৪৫ (V3) + সাম্প্রতিক ৪৬/৪৭/৫০ = ১৪টি বিসিএস।
    const years = [35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 50];

    // বার চার্ট: ১৪টি বিসিএসের সর্বমোট প্রশ্ন অনুযায়ী শীর্ষ ১০।
    const mathChart = computed(() => [...mathData].sort((a, b) => b.total - a.total).slice(0, 10));
    const mentalChart = computed(() => [...mentalData].sort((a, b) => b.total - a.total).slice(0, 10));

    // ── সিলেবাস অনুযায়ী টপিক গ্রুপ — গাণিতিক যুক্তি (২০ মার্ক); টপিক ক্লাস্টার V3 থেকে ──
    const mathSyllabus = [
      { marks: 4, topics: 'বাস্তব সংখ্যা, লসাগু, গসাগু, শতকরা, সরল ও যৌগিক মুনাফা, অনুপাত-সমানুপাত, লাভ-ক্ষতি' },
      { marks: 4, topics: 'বীজগাণিতিক সূত্রাবলি, বহুপদী উৎপাদক, সরল ও দ্বিপদী সমীকরণ, সরল ও দ্বিপদী অসমতা, সরল সহসমীকরণ' },
      { marks: 4, topics: 'সূচক ও লগারিদম, সমান্তর ও গুণোত্তর অনুক্রম ও ধারা' },
      { marks: 4, topics: 'রেখা, কোণ, ত্রিভুজ ও চতুর্ভুজ সংক্রান্ত উপপাদ্য, পিথাগোরাসের উপপাদ্য, বৃত্ত সংক্রান্ত উপপাদ্য, পরিমিতি — সরল ক্ষেত্র ও ঘনবস্তু' },
      { marks: 4, topics: 'সেট, বিন্যাস ও সমাবেশ, পরিসংখ্যান ও সম্ভাব্যতা' },
    ];

    const refBooks = [
      { title: 'সাধারণ গণিত (৬ষ্ঠ–১০ম শ্রেণি)', author: 'বোর্ড বই' },
      { title: 'উচ্চতর গণিত (৯ম–১০ম শ্রেণি)', author: 'সেট, বিন্যাস-সমাবেশ, পরিসংখ্যান ও সম্ভাব্যতা' },
      { title: 'যে কোনো এক সেট গাইড বই', author: 'প্রশ্নের ধরন বোঝার জন্য' },
      { title: 'গণিত ও মানসিক দক্ষতা — বিগত বছরের প্রশ্ন', author: 'PSC / বিসিএস প্রশ্ন বিশ্লেষণ' },
      { title: 'বিসিএস প্রস্তুতি অ্যাপ — গণিত সেকশন', author: 'টপিকভিত্তিক প্রশ্ন ও ব্যাখ্যা' },
      { title: 'বিসিএস প্রস্তুতি অ্যাপ — মানসিক দক্ষতা সেকশন', author: 'টপিকভিত্তিক প্রশ্ন ও ব্যাখ্যা' },
    ];

    const tips = [
      'প্রথমেই বিসিএস প্রস্তুতি অ্যাপ থেকে গণিত ও মানসিক দক্ষতার বিগত বছরের প্রশ্নগুলো ব্যাখ্যাসহ পড়ে ফেলুন। এতে কী টাইপের প্রশ্ন আসে তার সামগ্রিক ধারণা পাবেন — তখন নিজেই বুঝতে পারবেন কী কী পড়া লাগবে আর কী লাগবে না।',
      'বিগত বছরের প্রশ্ন বিশ্লেষণ ভালোভাবে দেখুন এবং সেইভাবে বেসিক থেকে পড়ুন। যেসব টপিক থেকে বেশি প্রশ্ন হয় সেগুলো খুব ভালোভাবে গভীর থেকে পড়ুন।',
      'গণিতের জন্য ৯ম-১০ম শ্রেণির বোর্ড বইটি বেসিক থেকে টপিক ধরে ধরে একবারে শেষ করে ফেলুন যেন কোনো ফাঁক-ফোকর না থাকে। এরপর PSC-র নেওয়া বিগত বছরের সকল প্রশ্ন ব্যাখ্যাসহ পড়ে ফেলুন।',
      'টপিক অনুসারে বেসিক থেকে পড়ুন — যে কোনো একটি টপিক ধরে ভালোভাবে অনুশীলন করুন। অ্যাপে সম্পূর্ণ গণিত ও মানসিক দক্ষতা টপিক/সাব-টপিক ও চ্যাপ্টার অনুসারে সাজানো আছে এবং স্মার্ট সিস্টেম আপনার প্রগ্রেস ট্র্যাক করবে।',
      'অনুশীলন শেষ হলে অ্যাপ থেকে যত খুশি বিষয়ভিত্তিক পরীক্ষা দিয়ে নিজের দুর্বলতা যাচাই করুন। মনে রাখবেন — বিসিএসে পরীক্ষা দেওয়ার কোনো বিকল্প নেই। কনফিউশন হলে সার্চ অপশনে খুঁজে পড়ুন ও গুরুত্বপূর্ণ প্রশ্ন মার্ক করে বার বার রিভিশন দিন।',
      'বিসিএস প্রস্তুতি অ্যাপের যে কোনো একটি লাইভ কোর্স (১২০ দিন বা ৬ মাস) ভালোভাবে ফলো করুন। ফাইনালের আগের ক্র্যাশ কোর্স ও চূড়ান্ত মডেল টেস্টগুলো অবশ্যই দিন।',
    ];

    // ── Helpers ──
    const toBengali = (n) => {
      const bn = ['০','১','২','৩','৪','৫','৬','৭','৮','৯'];
      return String(n).split('').map(c => bn[parseInt(c)] ?? c).join('');
    };
    const heatClass = (val) => { if (!val||val===0) return 'h0'; if (val===1) return 'h1'; if (val===2) return 'h2'; return 'h3'; };
    const priorityLabel = (p) => ({ 3:'★★★', 2:'★★', 1:'★' }[p]);

    // ── Funnel events (centralized in @/config/pixels) ──
    const trackAppInstall = () => pixels.trackAppInstallClick('math');
    const trackInitiateCheckout = () => pixels.trackInitiateCheckout('math');

    const setMeta = (attrs) => {
      const selector = attrs.property ? `meta[property="${attrs.property}"]` : `meta[name="${attrs.name}"]`;
      let el = document.querySelector(selector);
      if (!el) { el = document.createElement('meta'); document.head.appendChild(el); }
      Object.entries(attrs).forEach(([k,v]) => el.setAttribute(k,v));
    };

    onMounted(() => {
      window.scrollTo(0,0);
      document.title = 'গাণিতিক যুক্তি ও মানসিক দক্ষতা গাইডলাইন — টপিকভিত্তিক প্রশ্ন বিশ্লেষণ (৩৫–৫০ বিসিএস)';
      setMeta({name:'description', content:'বিসিএস গাণিতিক যুক্তি ও মানসিক দক্ষতার একুরেট টপিকভিত্তিক প্রশ্ন বিশ্লেষণ (বিসিএস ৩৫–৫০)। সাম্প্রতিক ৪৬/৪৭/৫০ সহ ১১টি বিসিএসের হিটম্যাপ, গুরুত্বপূর্ণ টপিক, রেফারেন্স বই ও প্রস্তুতি কৌশল।'});
      setMeta({name:'keywords', content:'গাণিতিক যুক্তি গাইডলাইন, মানসিক দক্ষতা গাইডলাইন, বিসিএস গণিত, BCS Math, BCS Mental Ability, বিসিএস প্রস্তুতি, বিন্যাস ও সমাবেশ, সূচক ও লগারিদম'});
      setMeta({name:'robots', content:'index, follow'});
      setMeta({property:'og:title', content:'গাণিতিক যুক্তি ও মানসিক দক্ষতা গাইডলাইন — টপিকভিত্তিক প্রশ্ন বিশ্লেষণ'});
      setMeta({property:'og:description', content:'বিসিএস ৩৫–৫০ গাণিতিক যুক্তি ও মানসিক দক্ষতার সম্পূর্ণ হিটম্যাপ বিশ্লেষণ।'});
      setMeta({property:'og:url', content:'https://www.bcsprostuti.com/math-guideline'});
      setMeta({property:'og:type', content:'article'});
      setMeta({property:'og:image', content:'https://www.bcsprostuti.com/og/math-guideline.jpg'});
      setMeta({property:'og:image:width', content:'1200'});
      setMeta({property:'og:image:height', content:'630'});
      setMeta({property:'og:image:alt', content:'গাণিতিক যুক্তি ও মানসিক দক্ষতা গাইডলাইন'});
      setMeta({property:'og:site_name', content:'বিসিএস প্রস্তুতি'});
      setMeta({name:'twitter:card', content:'summary_large_image'});
      setMeta({name:'twitter:title', content:'গাণিতিক যুক্তি ও মানসিক দক্ষতা গাইডলাইন — টপিকভিত্তিক প্রশ্ন বিশ্লেষণ'});
      setMeta({name:'twitter:description', content:'বিসিএস ৩৫–৫০ গাণিতিক যুক্তি ও মানসিক দক্ষতার সম্পূর্ণ হিটম্যাপ বিশ্লেষণ।'});
      setMeta({name:'twitter:image', content:'https://www.bcsprostuti.com/og/math-guideline.jpg'});
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) { canonical = document.createElement('link'); canonical.rel='canonical'; document.head.appendChild(canonical); }
      canonical.href = 'https://www.bcsprostuti.com/math-guideline';
      pixels.trackGuidelineView('math');
      pixels.armViewedGuideline50('math');
    });

    return { mathData, mentalData, mathSyllabus, years, mathChart, mentalChart, refBooks, tips, toBengali, heatClass, priorityLabel, trackAppInstall, trackInitiateCheckout };
  }
};
</script>

<style scoped>
* { box-sizing: border-box; }

.hero-section { position: relative; background: linear-gradient(140deg,#191B47 0%,#0d0f2e 55%,#062033 100%); padding: 80px 0 60px; overflow: hidden; }
.hero-noise { position: absolute; inset: 0; pointer-events: none; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E"); }
.hero-inner { position: relative; text-align: center; }
.hero-badge { display: inline-block; background: rgba(0,149,255,0.15); border: 1px solid rgba(0,149,255,0.4); color: #0095FF; font-size: 0.8rem; font-weight: 600; padding: 6px 18px; border-radius: 999px; letter-spacing: 0.05em; margin-bottom: 20px; }
.hero-title { font-family: 'Noto Serif Bengali', serif; font-size: clamp(2rem,5vw,3.2rem); font-weight: 900; color: #fff; line-height: 1.25; margin-bottom: 16px; }
.hero-accent { background: linear-gradient(90deg,#0095FF,#00c6ff); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
.hero-sub { font-size: 1.05rem; color: #94a3b8; margin-bottom: 40px; }
.hero-stats { display: flex; align-items: center; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); border-radius: 16px; padding: 16px 20px; backdrop-filter: blur(12px); flex-wrap: wrap; justify-content: center; row-gap: 12px; }
.hstat { text-align: center; padding: 0 16px; }
.hstat-num { font-family: 'Noto Serif Bengali', serif; font-size: clamp(1.3rem,3vw,2rem); font-weight: 900; color: #0095FF; line-height: 1; }
.hstat-lbl { font-size: 0.72rem; color: #94a3b8; margin-top: 4px; }
.hstat-sep { width: 1px; height: 40px; background: rgba(255,255,255,0.15); flex-shrink: 0; }
@media(max-width:768px) { .hstat-sep { display: none; } .hstat { padding: 4px 12px; } }

/* marks bar */
.marks-bar { background: #191B47; padding: 20px 0; }
.marks-header { font-size: 0.75rem; font-weight: 700; color: #94a3b8; text-align: center; margin-bottom: 14px; letter-spacing: 0.05em; text-transform: uppercase; }
.marks-grid { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; }
.mk-item { display: flex; flex-direction: column; align-items: center; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12); border-radius: 10px; padding: 10px 16px; min-width: 90px; }
.mk-num { font-family: 'Noto Serif Bengali', serif; font-size: 1.3rem; font-weight: 800; color: #0095FF; line-height: 1; }
.mk-lbl { font-size: 0.68rem; color: #94a3b8; margin-top: 5px; text-align: center; }

/* legend */
.legend-bar { background: #f8fafc; border-bottom: 1px solid #e2e8f0; padding: 10px 16px; overflow-x: auto; }
.legend-inner {
  display: flex; align-items: center; justify-content: flex-start; gap: 8px; font-size: 0.82rem; white-space: nowrap; min-width: max-content;
}
@media(min-width: 769px) {
  .legend-inner {
    justify-content: center;
    margin: 0 auto;
  }
}
.legend-label { font-weight: 700; color: #374151; margin-right: 4px; }
.lc { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 26px; border-radius: 6px; font-size: 0.78rem; font-weight: 700; border: 1px solid #e2e8f0; }
.h0 { background: #f1f5f9; color: #94a3b8; }
.h1 { background: #fef9c3; color: #854d0e; }
.h2 { background: #fed7aa; color: #7c2d12; }
.h3 { background: #fca5a5; color: #7f1d1d; font-weight: 800; }
.legend-sep { color: #cbd5e1; margin: 0 4px; }
.pri-tag { font-size: 0.76rem; font-weight: 700; padding: 3px 10px; border-radius: 999px; }
.pri-tag.p3 { background: #fef2f2; color: #991b1b; }
.pri-tag.p2 { background: #fffbeb; color: #92400e; }
.pri-tag.p1 { background: #f0fdf4; color: #166534; }

/* main */
.main-section { padding: 40px 0; }
.tab-content {
  animation: fadeIn 0.2s ease;
  width: 100% !important;
  max-width: 100% !important;
  overflow: hidden !important;
}
@keyframes fadeIn { from { opacity:0; transform: translateY(6px); } to { opacity:1; transform: none; } }

/* subject tag */
.subj-tag { display: inline-block; font-family: 'Noto Serif Bengali', serif; font-size: 0.9rem; font-weight: 800; color: #fff; background: #191B47; border-radius: 999px; padding: 7px 20px; margin-bottom: 20px; box-shadow: 0 4px 14px rgba(25,27,71,0.18); }
.subj-tag.mt { margin-top: 20px; }

/* chart */
.chart-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 16px; padding: 28px 32px; margin-bottom: 40px; box-shadow: 0 2px 12px rgba(0,0,0,0.05); overflow: hidden; }
.chart-title { font-size: 1rem; font-weight: 700; color: #191B47; margin-bottom: 24px; }
.syllabus-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 16px; padding: 28px 32px; margin-bottom: 40px; box-shadow: 0 2px 12px rgba(0,0,0,0.05); }
.syl-list { display: flex; flex-direction: column; gap: 12px; }
.syl-row { display: flex; align-items: center; gap: 16px; padding: 14px 16px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; }
.syl-mark { flex: 0 0 auto; width: 48px; height: 48px; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #0095FF; color: #fff; border-radius: 10px; }
.sm-num { font-size: 1.15rem; font-weight: 800; line-height: 1; }
.sm-lbl { font-size: 0.58rem; font-weight: 700; letter-spacing: 0.3px; opacity: 0.9; margin-top: 2px; }
.syl-topics { flex: 1; font-size: 0.95rem; font-weight: 500; color: #334155; line-height: 1.5; }
.bar-list { display: flex; flex-direction: column; gap: 10px; }
.bar-row { display: flex; align-items: center; gap: 10px; }
.bar-label { font-size: 0.78rem; font-weight: 600; color: #374151; width: 200px; flex-shrink: 0; text-align: right; }
.bar-track { flex: 1; height: 22px; background: #f1f5f9; border-radius: 6px; overflow: hidden; }
.bar-fill { height: 100%; border-radius: 6px; transition: width 0.8s cubic-bezier(0.4,0,0.2,1); }
.bp3 { background: linear-gradient(90deg,#DC2626,#f87171); }
.bp2 { background: linear-gradient(90deg,#D97706,#fbbf24); }
.bp1 { background: linear-gradient(90deg,#059669,#34d399); }
.bar-val { font-family: 'Noto Serif Bengali', serif; font-size: 0.85rem; font-weight: 700; color: #191B47; width: 28px; text-align: right; }
@media(max-width: 600px) {
  .bar-row { flex-wrap: wrap; gap: 6px 8px; }
  .bar-label { width: 100%; text-align: left; font-size: 0.8rem; font-weight: 700; flex-shrink: 0; }
  .bar-track { flex: 1; min-width: 0; }
  .bar-val { width: 28px; flex-shrink: 0; }
  .chart-card { padding: 16px 12px; }
}

/* section head */
.section-head { margin-bottom: 20px; }
.section-head h2 { font-size: 0.95rem; font-weight: 700; color: #191B47; margin: 0 0 3px 0; border-left: 3px solid #0095FF; display: block; padding: 2px 0 2px 10px; }
.section-head p { color: #64748b; font-size: 0.8rem; margin: 0; padding-left: 13px; }
.section-head.center { text-align: center; }
.section-head.center h2 { border-left: none; padding-left: 0; font-size: 1.1rem; border-bottom: 3px solid #0095FF; display: inline-block; padding-bottom: 4px; }
.section-head.center p { padding-left: 0; margin-top: 8px; }

/* table */
.table-wrap {
  width: 100% !important;
  max-width: 100% !important;
  display: block !important;
  overflow-x: scroll !important;
  overflow-y: hidden !important;
  -webkit-overflow-scrolling: touch !important;
  border-radius: 14px; box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  margin-bottom: 36px;
}
.table-wrap::-webkit-scrollbar {
  height: 6px;
  display: block;
}
.table-wrap::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.table-wrap::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}
.heat-table {
  width: 100% !important;
  max-width: none !important;
  min-width: 980px !important;
  border-collapse: collapse !important;
  font-size: 0.82rem; background: #fff;
}
.heat-table thead tr { background: #1e2a6e; }
.heat-table th { color: #e2e8f0; padding: 11px 7px; text-align: center; font-weight: 700; white-space: nowrap; font-size: 0.76rem; border-right: 1px solid rgba(255,255,255,0.1); }
.heat-table th.t-topic {
  text-align: left; padding-left: 16px; min-width: 210px; color: #fff;
  position: sticky; left: 0; background: #1e2a6e !important; z-index: 3;
  pointer-events: none;
  overflow: hidden; text-overflow: ellipsis; max-width: 210px;
}
@media(max-width: 768px) {
  .heat-table th.t-topic {
    min-width: 150px !important;
    max-width: 150px !important;
    padding-left: 8px;
  }
}
.heat-table th.t-new { background: #0095FF; color: #fff; }
.heat-table td.t-recent { box-shadow: inset 0 0 0 9999px rgba(0,149,255,0.07); }
.heat-table th, .heat-table td { min-width: 34px; }
.heat-table th.t-hist { background: #1d4ed8; color: #fff; min-width: 56px; line-height: 1.15; }
.heat-table th.t-appr { background: #0e7490; color: #fff; min-width: 56px; line-height: 1.15; }
.heat-table th.t-pri { background: #6d28d9; color: #fff; min-width: 74px; }
.heat-table td { padding: 7px 6px; text-align: center; border-bottom: 1px solid #f1f5f9; font-size: 0.8rem; font-family: 'Noto Serif Bengali', serif; }
.heat-table tbody tr:hover td { filter: brightness(0.96); }
.heat-table td.t-name {
  text-align: left !important; padding-left: 14px; font-weight: 600; color: #1e293b; white-space: nowrap; font-size: 0.8rem; font-family: system-ui, -apple-system, sans-serif;
  position: sticky; left: 0; background: #fff !important; z-index: 2;
  box-shadow: 2px 0 5px rgba(0,0,0,0.05);
  pointer-events: none;
  overflow: hidden; text-overflow: ellipsis; max-width: 210px;
}
@media(max-width: 768px) {
  .heat-table td.t-name {
    min-width: 150px !important;
    max-width: 150px !important;
    padding-left: 8px;
    font-size: 0.75rem;
  }
}
.t-hist-cell { background: #eff6ff; color: #1e40af; font-weight: 800; }
.t-appr-cell { background: #ecfeff; color: #0e7490; font-weight: 700; }
.t-na { background: #f8fafc; color: #cbd5e1; font-weight: 700; letter-spacing: 2px; }
.t-pri-cell { background: #faf5ff; }
.pb { display: inline-block; padding: 3px 9px; border-radius: 999px; font-size: 0.72rem; font-weight: 800; font-family: system-ui, -apple-system, sans-serif; }
.pb.p3 { background: #fef2f2; color: #991b1b; }
.pb.p2 { background: #fffbeb; color: #92400e; }
.pb.p1 { background: #f0fdf4; color: #166534; }

/* insight */
.insight-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 16px; margin-bottom: 32px; }
@media(max-width:768px) { .insight-grid { grid-template-columns: 1fr; } }
.ins-card { border-radius: 14px; padding: 22px 20px; text-align: left; }
.ins-head { font-weight: 800; font-size: 0.9rem; margin-bottom: 14px; text-align: left; }
.ins-card ul { padding-left: 18px; margin: 0; text-align: left; }
.ins-card li { font-size: 0.88rem; margin-bottom: 7px; color: #374151; text-align: left; word-break: break-word; }
.ins-card.must { background: #fef2f2; border: 1px solid #fecaca; }
.ins-card.must .ins-head { color: #991b1b; }
.ins-card.imp { background: #fffbeb; border: 1px solid #fde68a; }
.ins-card.imp .ins-head { color: #92400e; }
.ins-card.low { background: #f0fdf4; border: 1px solid #bbf7d0; }
.ins-card.low .ins-head { color: #166534; }

/* analysis */
.analysis-box { background: #f0f9ff; border-left: 4px solid #191B47; border-radius: 0 14px 14px 0; padding: 20px 24px; margin-bottom: 48px; text-align: left; }
.analysis-box h4 { font-size: 1rem; font-weight: 700; color: #191B47; margin-bottom: 10px; }
.analysis-box p { font-size: 0.9rem; color: #374151; margin-bottom: 10px; line-height: 1.7; }
.mb-0 { margin-bottom: 0; }

/* ref books */
.ref-section { background: #f8fafc; padding: 48px 0; }
.book-grid { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 12px; margin-top: 28px; width: 100%; }
@media(max-width:768px) { .book-grid { grid-template-columns: repeat(2,1fr); } }
@media(max-width:480px) { .book-grid { grid-template-columns: 1fr; } }
.book-card { display: flex; align-items: stretch; border-radius: 10px; overflow: hidden; border: 1px solid #e2e8f0; background: #fff; box-shadow: 0 2px 6px rgba(0,0,0,0.04); transition: transform 0.18s; }
.book-card:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(0,0,0,0.09); }
.book-spine { width: 5px; background: linear-gradient(180deg,#191B47,#0369a1); flex-shrink: 0; }
.book-info { padding: 12px 14px; display: flex; flex-direction: column; justify-content: center; text-align: left; }
.book-title { font-size: 0.82rem; font-weight: 700; color: #1e293b; margin-bottom: 3px; line-height: 1.4; }
.book-author { font-size: 0.74rem; color: #64748b; }

/* tips */
.tips-section { background: #fff; padding: 48px 0; }
.tips-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 12px; margin-top: 28px; }
@media(max-width:600px) { .tips-grid { grid-template-columns: 1fr; } }
.tip-card { display: flex; gap: 16px; align-items: flex-start; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 14px; padding: 20px; }
.tip-num { width: 32px; height: 32px; border-radius: 50%; background: #fff; color: #191B47; border: 2px solid #191B47; display: flex; align-items: center; justify-content: center; font-family: 'Noto Serif Bengali', serif; font-weight: 800; font-size: 0.85rem; flex-shrink: 0; }
.tip-text { font-size: 0.9rem; color: #374151; line-height: 1.7; margin: 0; text-align: left; }

/* CTA */
.cta-section { background: #191B47; border-top: 4px solid #0095FF; padding: 64px 0; }
.cta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; }
@media(max-width:768px) { .cta-grid { grid-template-columns: 1fr; gap: 32px; } }
.cta-left { align-self: center; }
.cta-tag { display: inline-block; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #0095FF; background: rgba(0,149,255,0.15); border: 1px solid rgba(0,149,255,0.35); padding: 4px 14px; border-radius: 999px; margin-bottom: 14px; }
.cta-title { font-family: 'Noto Serif Bengali', serif; font-size: 1.5rem; font-weight: 800; color: #fff; line-height: 1.4; margin-bottom: 20px; }
.cta-features { list-style: none; padding: 0; margin: 0 0 24px 0; display: flex; flex-direction: column; gap: 9px; }
.cta-features li { display: flex; align-items: center; gap: 10px; font-size: 0.88rem; color: #c7d2fe; }
.cf-icon { width: 20px; height: 20px; background: rgba(0,149,255,0.2); color: #0095FF; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.7rem; font-weight: 800; flex-shrink: 0; }
.cta-actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
.cta-play { display: inline-flex; align-items: center; background: #fff; border-radius: 10px; padding: 7px 16px; text-decoration: none; transition: transform 0.2s; }
.cta-play:hover { transform: scale(1.03); }
.cta-pkg { display: inline-flex; align-items: center; background: #0095FF; color: #1a1a1a; font-weight: 700; font-size: 0.9rem; border-radius: 10px; padding: 11px 24px; text-decoration: none; transition: all 0.2s; }
.cta-pkg:hover { background: #007AD6; transform: scale(1.02); }
.cta-count { font-size: 0.78rem; color: #64748b; margin: 0; }
.cta-right { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; align-self: center; }
@media(max-width:768px) { .cta-right { grid-template-columns: repeat(2, 1fr); gap: 10px; } }
.cta-stat-card { background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.14); border-radius: 14px; padding: 24px 20px; text-align: center; }
.cs-num { font-family: 'Noto Serif Bengali', serif; font-size: 1.6rem; font-weight: 800; color: #0095FF; line-height: 1.1; }
.cs-lbl { font-size: 0.75rem; color: #94a3b8; margin-top: 7px; }
</style>
